import { Cookies } from '@egw/clients'
import { v4 as uuid } from 'uuid'
import { Logger } from '@egw/logger'
import { EGM_A_COOKIE } from '@egw/constants'
import { configClient } from '@egw/config'

const log = new Logger('Enrollment Cookie Service')

const getCookieDomain = (env: string): string => {
  switch (env) {
    case 'prod':
      return '.evergreenwealth.com'
    case 'demo-prod':
      return '.evergreenwealth.com'
    case 'dev':
      return '.wealth-eg-dev.com'
    case 'demo-dev':
      return '.wealth-eg-dev.com'
    case 'local':
      return 'localhost'
    default:
      return '.wealth-eg-dev.com'
  }
}

let instance: EnrollmentCookieService | null = null

class EnrollmentCookieService {
  private sessionId: string | undefined

  constructor() {
    if (!instance) {
      instance = this
    }
    return instance
  }

  private getEnvironment(): string {
    const config = configClient.getConfig() as { env?: string } | undefined
    return config?.env || 'dev'
  }

  // Reset the enrollment session cookie - a-session-id that replaces e-session-id
  async resetSession(): Promise<void> {
    const env = this.getEnvironment()
    const sessionId = uuid()
    this.sessionId = sessionId
    const domain = getCookieDomain(env)

    log.debug('Starting enrollment cookie reset')

    try {
      const cookieOptions = {
        domain,
        // Enhanced security attributes (should also be applied to s-session-id)
        path: '/',
        secure: env !== 'local',
        sameSite: 'Lax' as const,
      }

      // Force delete old cookie first with multiple domain variations
      Cookies.remove(EGM_A_COOKIE, { domain, path: '/' })
      Cookies.remove(EGM_A_COOKIE, { domain: domain.replace(/^\./, ''), path: '/' })
      Cookies.remove(EGM_A_COOKIE, { path: '/' })

      // Set new cookie
      Cookies.set(EGM_A_COOKIE, sessionId, cookieOptions)

      // Poll until cookie is verified or max retries reached
      const maxRetries = 10
      const retryDelayMs = 50
      let verified = false

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        // Check both js-cookie and direct document.cookie
        const jsCookieValue = Cookies.get(EGM_A_COOKIE)
        const directCookieValue =
          typeof document !== 'undefined'
            ? document.cookie
                .split('; ')
                .find((row) => row.startsWith(`${EGM_A_COOKIE}=`))
                ?.split('=')[1]
            : undefined

        if (jsCookieValue === sessionId && directCookieValue === sessionId) {
          verified = true
          log.debug('Enrollment session reset verified', {
            sessionId,
            attempt: attempt + 1,
            totalTime: attempt * retryDelayMs,
          })
          break
        }

        // Not verified yet - retry
        if (attempt < maxRetries - 1) {
          log.debug('Cookie not yet verified, retrying', {
            attempt: attempt + 1,
            expected: sessionId,
            jsCookieValue,
            directCookieValue,
          })

          // Wait before next check
          await new Promise((resolve) => setTimeout(resolve, retryDelayMs))

          // Try setting again
          Cookies.set(EGM_A_COOKIE, sessionId, cookieOptions)
        }
      }

      // Final verification - throw if still not set
      if (!verified) {
        const finalJsValue = Cookies.get(EGM_A_COOKIE)
        const finalDirectValue =
          typeof document !== 'undefined'
            ? document.cookie
                .split('; ')
                .find((row) => row.startsWith(`${EGM_A_COOKIE}=`))
                ?.split('=')[1]
            : undefined

        const error = new Error('Cookie was not set properly after multiple attempts')
        log.error('Cookie reset failed after retries', {
          maxRetries,
        })

        throw error
      }
    } catch (error) {
      log.error('Failed to set enrollment cookie', {
        error,
        sessionId,
        domain,
        env,
        message: error instanceof Error ? error.message : 'Unknown error',
      })
      throw error
    }
  }

  getSessionId(): string | undefined {
    return this.sessionId
  }
}

export const enrollmentCookieService = new EnrollmentCookieService()
